import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getPortfolioById } from "./portfolios";
import {
  enrichStocks,
  sumStocks,
  sumInvestmentAmount,
  estimateCapitalGainsTax,
} from "./stocks";
import Tickers from "./Tickers";
import { ImArrowLeft } from "react-icons/im";
import { useNavigate, useLocation } from "react-router-dom";

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${(p) =>
    `linear-gradient(135deg, ${p.$color} 0%, #f3fbe6 25%, #e6f6ff 65%, #fef9e3 100%)`};
  position: relative;
`;

const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px 10px;
  color: #fff;
  position: sticky;
  top: 0;
  backdrop-filter: blur(8px);
`;

const BackBtn = styled.button`
  background: #ffffff20;
  border: 1px solid #ffffff35;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  cursor: pointer;
  color: #fff;
  transition:
    background 0.25s,
    transform 0.25s;
  &:hover {
    background: #ffffff35;
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const Title = styled.h1`
  font-size: clamp(1.2rem, 3vw, 1.9rem);
  margin: 0;
`;

const RankBadge = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: ${(p) => {
    if (p.$rank === 1) return "linear-gradient(145deg,#ffe27a,#ffcc33)";
    if (p.$rank === 2) return "linear-gradient(145deg,#e0e5ec,#c9d0d7)";
    if (p.$rank === 3) return "linear-gradient(145deg,#f6c39b,#e19152)";
    return "linear-gradient(145deg,#d4e8f5,#b7d2e5)";
  }};
  color: #2b3742;
  font-size: 0.55rem;
  font-weight: 800;
  padding: 8px 10px 8px;
  border-radius: 18px;
  letter-spacing: 0.8px;
  box-shadow:
    0 4px 10px -4px rgba(0, 0, 0, 0.28),
    0 0 0 2px #ffffffaa;
  text-transform: uppercase;
  line-height: 1;
  min-width: 54px;
  font-family: "Baloo 2", "Fredoka", "Comic Sans MS", sans-serif;
`;

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const MetricsStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 4px 24px 14px;
`;

const MetricCard = styled.div`
  flex: 1 1 180px;
  background: linear-gradient(145deg, #ffffff, #f2f9ff 45%, #ecf9f0);
  border: 3px solid #d2e8f3;
  padding: 18px 18px 22px;
  border-radius: 20px;
  color: #233642;
  position: relative;
  overflow: hidden;
  min-width: 150px;
  box-shadow: 0 6px 14px -6px rgba(20, 54, 78, 0.18);
  font-family: "Baloo 2", "Fredoka", "Comic Sans MS", sans-serif;
  &:before {
    content: "\u269C";
    position: absolute;
    right: 10px;
    top: 10px;
    opacity: 0.35;
    font-size: 1.25rem;
    color: #2d6f8c;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: 0.75;
  font-weight: 700;
`;

const MetricValue = styled.div`
  font-size: 1.55rem;
  font-weight: 700;
  margin-top: 2px;
`;

const Content = styled.div`
  padding: 10px 20px 60px;
  flex: 1;
`;

const Loading = styled.div`
  padding: 60px 30px;
  text-align: center;
  font-size: 1.1rem;
  opacity: 0.85;
`;

const WarningBanner = styled.div`
  margin: 20px 0 28px; /* keep aligned with App */
  padding: 16px 20px 18px;
  border: 3px solid #ff5a5a;
  background:
    linear-gradient(180deg, #ff3b3b, #ff8a8a) 0 0/6px 100% no-repeat,
    linear-gradient(135deg, #fff2f2 0%, #ffe0e0 55%, #ffd6d6 100%);
  color: #7a0b0b;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.6px;
  border-radius: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  box-shadow:
    0 6px 16px -6px rgba(122, 11, 11, 0.45),
    0 0 0 3px #ffffffaa;
  font-family: "Baloo 2", "Fredoka", "Comic Sans MS", sans-serif;
`;

const ViewToggleBtn = styled.button`
  margin-left: auto;
  background: linear-gradient(145deg, #fffbe2, #e2f5ff);
  border: 2px solid #c3e3ef;
  color: #2b4d5e;
  padding: 12px 18px;
  border-radius: 16px;
  font-size: 0.7rem;
  letter-spacing: 0.6px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  transition:
    background 0.25s,
    transform 0.25s;
  font-family: "Baloo 2", "Fredoka", "Comic Sans MS", sans-serif;
  &:hover {
    background: linear-gradient(145deg, #fff4c5, #d8f1ff);
    transform: translateY(-3px) rotate(-1deg);
  }
  &:active {
    transform: translateY(-1px);
  }
`;

const GameBtn = styled.button`
  background: linear-gradient(145deg, #d8ffe2, #fff7d9 55%, #e3f4ff);
  border: 2px solid #c2e9d2;
  color: #20503a;
  padding: 12px 18px;
  border-radius: 16px;
  font-size: 0.7rem;
  letter-spacing: 0.6px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  transition:
    background 0.25s,
    transform 0.25s;
  font-family: "Baloo 2", "Fredoka", "Comic Sans MS", sans-serif;
  &:hover {
    background: linear-gradient(145deg, #c7ffd3, #fff1bb 55%, #daf0ff);
    transform: translateY(-3px) rotate(1deg);
  }
  &:active {
    transform: translateY(-1px);
  }
`;

const Emoji = ({ label, symbol, fallback = "💰" }) => (
  <span
    role="img"
    aria-label={label}
    style={{
      fontFamily:
        "Apple Color Emoji,Segoe UI Emoji,Noto Color Emoji,Segoe UI Symbol,sans-serif",
      display: "inline-block",
      lineHeight: 1,
    }}
  >
    {symbol || fallback}
  </span>
);

export default function PortfolioView({ id, onBack }) {
  const portfolio = getPortfolioById(id);
  const location = useLocation();
  const rank = location?.state?.rank;
  const totalRank = location?.state?.totalRank;
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [condensed, setCondensed] = useState(true); // toggle between compact and detailed
  const [selectedTicker, setSelectedTicker] = useState(undefined); // ticker symbol chosen from condensed view
  const [priceWarning, setPriceWarning] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const enriched = await enrichStocks(portfolio.data);
        if (active) {
          setStocks(enriched);
          if (enriched.some((s) => s._priceFetchFailed)) setPriceWarning(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, portfolio.data]);

  const total = stocks.length ? sumStocks(stocks) : 0;
  const invested = stocks.length ? sumInvestmentAmount(stocks) : 0;
  const delta = total - invested;
  const pct = invested === 0 ? 0 : (delta / invested) * 100;
  const negative = delta < 0;
  const friendlyDeltaLabel = negative ? "Down" : "Up";
  const estTax = estimateCapitalGainsTax({
    totalValue: total,
    investedAmount: invested,
  });

  const handleSelectTicker = (ticker) => {
    // User clicked a condensed card: expand to detailed view and focus that ticker
    setSelectedTicker(ticker);
    setCondensed(false);
  };

  const handleToggleView = () => {
    setCondensed((c) => {
      const next = !c;
      // If switching back to condensed, clear any prior selection
      if (next) {
        setSelectedTicker(undefined);
      }
      return next;
    });
  };
  const navigate = useNavigate();

  return (
    <Wrapper $color={portfolio.color}>
      <HeaderBar>
        <BackBtn aria-label="Back" onClick={onBack}>
          <ImArrowLeft />
        </BackBtn>
        <Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {portfolio.name}'s Portfolio
          {rank && (
            <RankBadge
              $rank={rank}
              aria-label={`Rank ${rank} of ${totalRank || ""}`}
            >
              <span style={{ fontSize: "0.9rem" }} role="img" aria-hidden>
                {rank === 1
                  ? "🥇"
                  : rank === 2
                    ? "🥈"
                    : rank === 3
                      ? "🥉"
                      : "🏅"}
              </span>
              {ordinal(rank)}
            </RankBadge>
          )}
        </Title>
        <ViewToggleBtn
          type="button"
          aria-pressed={condensed}
          onClick={handleToggleView}
          title={condensed ? "Show Bigger Cards" : "Show Small Cards"}
        >
          {condensed ? "Big View 🔍" : "Small View 🧩"}
        </ViewToggleBtn>
        <GameBtn
          type="button"
          onClick={() => navigate(`/portfolio/${portfolio.id}/game`)}
          title="Play a fun guessing game"
        >
          Play Game 🎮
        </GameBtn>
      </HeaderBar>
      {priceWarning && (
        <WarningBanner role="alert" aria-live="polite">
          <span role="img" aria-hidden style={{ fontSize: "1rem" }}>
            ⚠️
          </span>
          <span>
            Some live prices failed to load for this portfolio. Those tickers
            show purchase cost as a fallback.
          </span>
        </WarningBanner>
      )}

      <MetricsStrip>
        <MetricCard>
          <MetricLabel>
            Treasure <Emoji label="treasure" symbol="💰" />
          </MetricLabel>
          <MetricValue>${total.toFixed(2)}</MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>
            Put In <Emoji label="invested" symbol="💵" />
          </MetricLabel>
          <MetricValue>${invested.toFixed(2)}</MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>
            {friendlyDeltaLabel}{" "}
            <Emoji label="performance" symbol={negative ? "📉" : "📈"} />
          </MetricLabel>
          <MetricValue style={{ color: negative ? "#e74c3c" : "#1e9e52" }}>
            {negative ? "-" : "+"}${Math.abs(delta).toFixed(2)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>
            Growth % <Emoji label="growth" symbol={negative ? "🌧️" : "🌱"} />
          </MetricLabel>
          <MetricValue style={{ color: negative ? "#e74c3c" : "#1e9e52" }}>
            {negative ? "-" : "+"}
            {Math.abs(pct).toFixed(2)}%
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Est. Taxes </MetricLabel>
          <MetricValue style={{ color: "#e74c3c" }}>
            ${estTax.toFixed(2)}
          </MetricValue>
        </MetricCard>
      </MetricsStrip>

      <Content>
        {loading ? (
          <Loading>Loading live prices…</Loading>
        ) : (
          <Tickers
            data={stocks}
            name={portfolio.name}
            condensed={condensed}
            onSelectTicker={handleSelectTicker}
            focusTicker={selectedTicker}
          />
        )}
      </Content>
    </Wrapper>
  );
}
